import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PAGE_META_LOCALES, PageMeta as PageMetaType } from "../seo/pageMeta";
import {
  stripLangPrefix,
  getPathFromLang,
  LangPath,
} from "../i18n/routing";

function upsertMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonicalLink(canonicalUrl: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", canonicalUrl);
}

function removeCanonicalLink() {
  const el = document.querySelector('link[rel="canonical"]');
  if (el) {
    el.remove();
  }
}

function upsertHreflangLink(hreflang: string, href: string) {
  let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function removeAllHreflangLinks() {
  const elements = document.querySelectorAll('link[rel="alternate"][hreflang]');
  elements.forEach((el) => el.remove());
}

export type PageMetaProps = PageMetaType & {
  path?: string;
  noindex?: boolean;
};

const SITE_ORIGIN = "https://wesoft.co.jp";

function getCleanPurePath(pathProp?: string): string {
  let currentPath = pathProp;
  if (!currentPath && typeof window !== "undefined" && window.location) {
    currentPath = window.location.pathname;
  }
  if (!currentPath) {
    return "/";
  }

  const purePath = stripLangPrefix(currentPath);
  if (purePath === "/" || !purePath) {
    return "/";
  }

  const clean = purePath.replace(/\/+$/, "");
  if (clean.startsWith("/column")) {
    return `${clean}/`;
  }
  return clean;
}

function getLocalizedFullUrl(purePath: string, langPath: LangPath): string {
  if (purePath === "/") {
    return `${SITE_ORIGIN}/${langPath}/`;
  }
  return `${SITE_ORIGIN}/${langPath}${purePath}`;
}

const PageMeta: React.FC<PageMetaProps> = ({ title, description, path, noindex }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "ja").split("-")[0];
  const currentLangPath = getPathFromLang(lang);

  const currentMetaMap = PAGE_META_LOCALES[lang] || PAGE_META_LOCALES.ja;

  const purePath = getCleanPurePath(path);

  let finalTitle = title;
  let finalDescription = description;

  if (path && currentMetaMap[path]) {
    finalTitle = currentMetaMap[path].title;
    finalDescription = currentMetaMap[path].description;
  } else if (currentMetaMap[purePath]) {
    finalTitle = currentMetaMap[purePath].title;
    finalDescription = currentMetaMap[purePath].description;
  }

  useEffect(() => {
    document.title = finalTitle;
    upsertMetaName("description", finalDescription);
    upsertMetaProperty("og:title", finalTitle);
    upsertMetaProperty("og:description", finalDescription);
    upsertMetaProperty("og:type", purePath.startsWith("/column/") ? "article" : "website");

    if (noindex) {
      upsertMetaName("robots", "noindex, nofollow");
      removeCanonicalLink();
      removeAllHreflangLinks();
    } else {
      upsertMetaName("robots", "index, follow");

      if (purePath.startsWith("/column")) {
        const canonicalUrl = `${SITE_ORIGIN}${purePath}`;
        upsertCanonicalLink(canonicalUrl);
        upsertMetaProperty("og:url", canonicalUrl);
        removeAllHreflangLinks();
      } else {
        const canonicalUrl = purePath === "/" ? `${SITE_ORIGIN}/` : getLocalizedFullUrl(purePath, currentLangPath);
        upsertCanonicalLink(canonicalUrl);
        upsertMetaProperty("og:url", canonicalUrl);

        upsertHreflangLink("ja", `${SITE_ORIGIN}/`);
        upsertHreflangLink("en", getLocalizedFullUrl(purePath, "us"));
        upsertHreflangLink("zh-CN", getLocalizedFullUrl(purePath, "cn"));
        upsertHreflangLink("x-default", `${SITE_ORIGIN}/`);
      }
    }
  }, [finalTitle, finalDescription, currentLangPath, purePath, noindex]);

  return null;
};

export default PageMeta;
